// shellTop — THE FRAME'S TOP HAS ONE OWNER (FRONTEND-PATTERNS §Navigation / shell).
//
// "Where does the pinned top chrome end?" is one question, and this module owns
// its answer. A frame pins ONE block at the window's top — the project-scope
// strips (demo / trial) stacked over its bar — and that block MEASURES ITSELF
// here (`ShellTop`). Nothing else may assume a height: a strip wraps to two
// lines on a phone or in Arabic, and a second strip can appear while a page is
// open. A private "the bar is 56px from the window's top" is how a peek ends up
// painted over the navbar the day a strip shows.
//
// What it publishes on :root — index.css holds the AT-REST values (no strip, the
// bar alone), and they return the moment the owner unmounts:
//   --shell-strips   the strips' height: where the frame's bar starts — and the
//                    condensed bar that overlays it. 0 at rest.
//   --shell-top      strips + bar: where the page starts (the peek, the section
//                    rails). The bar's height at rest.
//   --dt-sticky-top  where a viewport-pinned surface lands (a thead, group bands,
//                    sticky days, a panel docked beside a table): `--shell-top`
//                    at rest; the condensed bar's BOTTOM EDGE while one is
//                    mounted. (Not the wide peek — an overlay ABOVE the condensed
//                    bar, it starts at `--shell-top`.)
// ALWAYS a plain px length, never calc(): DataTable parses `--dt-sticky-top`
// back into a number for its stuck-state observer.
//
// The same numbers are readable from JS (`useShellTopPx` · `useStickyTopPx`): an
// IntersectionObserver's rootMargin and the landing rule's scroll maths take a
// px NUMBER — a CSS variable cannot feed them.

import { useLayoutEffect, type RefObject } from 'react';
import { create } from 'zustand';

/** The frame bar's at-rest height (`h-14` — the stylesheet's `3.5rem` at the
 *  default 16px root font) — what holds where no frame measures itself: a bare
 *  story, a test. A mounted frame always measures before any reader asks, so the
 *  running app never reads it. */
export const SHELL_BAR_REST_PX = 56;

interface ShellTopState {
  /** The mounted frame's measured top block — the strips' height, and the whole
   *  block's (strips + bar). `null` = none mounted: the at-rest values hold. */
  frame: { owner: object; stripsPx: number; topPx: number } | null;
  /** The mounted condensed bar's measured height — it covers the frame's bar,
   *  UNDER the strips. `null` = none. */
  condensedBar: { owner: object; heightPx: number } | null;
}

export const useShellTopStore = create<ShellTopState>()(() => ({ frame: null, condensedBar: null }));

const stripsOf = (s: ShellTopState): number => s.frame?.stripsPx ?? 0;
const topOf = (s: ShellTopState): number => s.frame?.topPx ?? SHELL_BAR_REST_PX;
const stickyTopOf = (s: ShellTopState): number => (s.condensedBar ? stripsOf(s) + s.condensedBar.heightPx : topOf(s));

/** Where the page starts — strips + bar, in px. */
export const useShellTopPx = (): number => useShellTopStore(topOf);
/** Where a viewport-pinned surface lands — under whatever bar owns the top, in px. */
export const useStickyTopPx = (): number => useShellTopStore(stickyTopOf);

// Sub-pixel heights are kept (a wrapped strip at a zoomed text size is rarely
// whole): an edge published to the hundredth coincides with the block's real
// edge, where a rounded one leaves a hairline that content scrolls through.
const px = (n: number): string => `${Math.round(n * 100) / 100}px`;

// The CSS first, the store second: whoever the store wakes — a React reader, a
// raw subscriber — finds the root already carrying the values it is told about.
function commit(next: Partial<ShellTopState>): void {
  const state = { ...useShellTopStore.getState(), ...next };
  if (typeof document !== 'undefined') {
    const root = document.documentElement.style;
    if (state.frame) {
      root.setProperty('--shell-strips', px(state.frame.stripsPx));
      root.setProperty('--shell-top', px(state.frame.topPx));
    } else {
      root.removeProperty('--shell-strips');
      root.removeProperty('--shell-top');
    }
    if (state.condensedBar) root.setProperty('--dt-sticky-top', px(stickyTopOf(state)));
    else root.removeProperty('--dt-sticky-top');
  }
  useShellTopStore.setState(next);
}

// One slot, one owner at a time — the latest to measure claims it. Two are never
// mounted together by design (the frames are exclusive route branches; an
// embedded list mounts no bar of its own), but a slot released UNDER a block that
// is still mounted — the newer owner left first — is claimed back, never left
// silent at the at-rest values.
function reclaimWhenReleased(slot: keyof ShellTopState, measure: () => void): () => void {
  return useShellTopStore.subscribe((state, previous) => {
    if (state[slot] === null && previous[slot] !== null) measure();
  });
}

/** Mount in a frame's pinned top block (`ShellTop`): measures the block and the
 *  strips region inside it BEFORE the first paint, then on every resize of
 *  either — a strip arriving with the project, wrapping, or leaving. */
export function useShellTopMeasure(blockRef: RefObject<HTMLElement | null>, stripsRef: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const block = blockRef.current;
    const strips = stripsRef.current;
    if (!block || !strips) return;
    const owner = {};
    const measure = () => {
      const topPx = block.getBoundingClientRect().height;
      const stripsPx = strips.getBoundingClientRect().height;
      const current = useShellTopStore.getState().frame;
      // Not laid out (jsdom · a hidden subtree): claim nothing — at rest is truer than zero.
      if (topPx <= 0) {
        if (current?.owner === owner) commit({ frame: null });
        return;
      }
      if (current?.owner === owner && current.topPx === topPx && current.stripsPx === stripsPx) return;
      commit({ frame: { owner, stripsPx, topPx } });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(block);
    observer?.observe(strips);
    const stopReclaim = reclaimWhenReleased('frame', measure);
    return () => {
      stopReclaim();
      observer?.disconnect();
      if (useShellTopStore.getState().frame?.owner === owner) commit({ frame: null });
    };
  }, [blockRef, stripsRef]);
}

/** Mount in a condensed bar: while `active`, the bar OWNS the pinned top — it
 *  covers the frame's bar under the strips, so pinned surfaces land flush under
 *  ITS real height (one row on `sm+`, two on a phone). Released on unmount; the
 *  strips moving under a mounted bar re-publish by themselves. */
export function useCondensedBarOwnsTop(barRef: RefObject<HTMLElement | null>, active = true): void {
  useLayoutEffect(() => {
    if (!active) return;
    const bar = barRef.current;
    if (!bar) return;
    const owner = {};
    const measure = () => {
      const heightPx = bar.getBoundingClientRect().height;
      const current = useShellTopStore.getState().condensedBar;
      if (heightPx <= 0) {
        if (current?.owner === owner) commit({ condensedBar: null });
        return;
      }
      if (current?.owner === owner && current.heightPx === heightPx) return;
      commit({ condensedBar: { owner, heightPx } });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(bar);
    const stopReclaim = reclaimWhenReleased('condensedBar', measure);
    return () => {
      stopReclaim();
      observer?.disconnect();
      if (useShellTopStore.getState().condensedBar?.owner === owner) commit({ condensedBar: null });
    };
  }, [barRef, active]);
}
